import { nativeWatchFacetParams } from '../../../app/Libraries/NativeWatchFilters';

/** Preserve native shopping state only when the server opts this route into a release. */
const VERSION = '20261008';
const STAGING_ORIGIN = 'https://raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com';
const PUBLIC_ORIGINS = new Set(['https://www.raffi-jewellers.ca', 'https://raffi-jewellers.ca']);
const EXACT_PATHS = new Set([
  '/', '/services', '/jewellery-repair', '/jewellery-cleaning', '/rhodium-plating',
  '/watch-repair', '/watch-battery-replacement', '/custom-design', '/engraving', '/appraisals',
  '/shopping-bag', '/checkout', '/search', '/search/products', '/jewellery-try-on',
  '/tudor', '/tudor-collection', '/tudor-watchmaking', '/tudor-ambassadors',
  '/tudor-at-raffi-jewellers', '/tudor/royalmount-montreal', '/tudor-contact-us',
  '/tudor-black-bay', '/tudor-pelagos', '/tudor-royal', '/tudor-ranger', '/tudor-1926',
]);

type ReleaseMarkers = { privateVersion?: string | null; publishedVersion?: string | null };

function canonicalPath(path: string): string {
  if (!path.startsWith('/') || /[%\\\x00-\x20]/.test(path) || path.includes('//')
    || /(?:^|[/-])rolex(?:[/-]|$)/i.test(path)) return '';
  return path.replace(/^\/fr(?=\/|$)/, '').replace(/\/$/, '') || '/';
}

function supportedPath(path: string): boolean {
  return !!path && (EXACT_PATHS.has(path)
    || /^\/collections\/(?:timepieces|fine-jewellery|engagement-rings|wedding-bands|anniversary-rings)(?:\/[a-z0-9-]+)*$/i.test(path)
    || /^\/products\/[a-z0-9-]+$/i.test(path));
}

function localizedPath(path: string, locale: string): string {
  return locale === 'fr' ? '/fr' + path : path;
}

export function nativeLanguageHref(current: string, locale: string | undefined, markers: ReleaseMarkers): string {
  if (locale !== 'en' && locale !== 'fr') return '';
  try {
    const url = new URL(current);
    const path = canonicalPath(url.pathname);
    // Accessory catalogues retain their existing layout without a release marker.
    // Change only their locale URL so an explicit lang query cannot override the click.
    const accessoryCollection = /^\/collections\/gifts-accessories(?:\/[a-z0-9-]+)*$/i.test(path)
      && !/(?:^|[/-])tag-?heuer(?:[/-]|$)/i.test(path);
    if (accessoryCollection && (PUBLIC_ORIGINS.has(url.origin) || url.origin === STAGING_ORIGIN)
      && !url.username && !url.password) {
      url.pathname = localizedPath(path, locale);
      url.searchParams.set('lang', locale);
      return url.href;
    }
    const published = PUBLIC_ORIGINS.has(url.origin) && markers.publishedVersion === VERSION;
    const active = url.origin === STAGING_ORIGIN && markers.privateVersion === VERSION || published;
    if (!active || url.username || url.password || !supportedPath(path)) return '';
    url.pathname = localizedPath(path, locale);
    url.searchParams.set('lang', locale);
    nativeWatchFacetParams(url.searchParams, locale);
    if (published) {
      url.searchParams.delete('drafts');
      url.searchParams.delete('preview');
    }

    // A product's return-to-selection link must remain valid after changing
    // language. Keep every catalogue filter/hash and reject cross-origin paths.
    const selection = url.searchParams.get('selection');
    if (selection && selection.length <= 4096 && selection.startsWith('/') && !selection.startsWith('//')) {
      const selected = new URL(selection, url.origin);
      const selectedPath = canonicalPath(selected.pathname);
      if (selected.origin === url.origin && /^\/collections\/(?:timepieces|fine-jewellery|engagement-rings|wedding-bands|anniversary-rings)(?:\/[a-z0-9-]+)*$/i.test(selectedPath)) {
        selected.pathname = localizedPath(selectedPath, locale);
        nativeWatchFacetParams(selected.searchParams, locale);
        if (selected.searchParams.has('lang')) selected.searchParams.set('lang', locale);
        if (published) {
          selected.searchParams.delete('drafts');
          selected.searchParams.delete('preview');
        }
        url.searchParams.set('selection', selected.pathname + selected.search + selected.hash);
      }
    }
    return url.href;
  } catch (_) { return ''; }
}

/** Keep authored accessory catalogue links on this site and in the rendered locale. */
export function accessoryCatalogueHref(current: string, href: string, locale: string | undefined): string {
  if ((locale !== 'en' && locale !== 'fr') || !href || href.startsWith('#')) return '';
  try {
    const currentUrl = new URL(current);
    const target = new URL(href, currentUrl);
    const accessoryPath = (url: URL): string => {
      if ((!PUBLIC_ORIGINS.has(url.origin) && url.origin !== STAGING_ORIGIN) || url.username || url.password) return '';
      const path = canonicalPath(url.pathname);
      return /^\/collections\/gifts-accessories(?:\/[a-z0-9-]+)*$/i.test(path)
        && !/(?:^|[/-])tag-?heuer(?:[/-]|$)/i.test(path) ? path : '';
    };
    const targetPath = accessoryPath(target);
    if (!accessoryPath(currentUrl) || !targetPath) return '';
    const localized = new URL(localizedPath(targetPath, locale), currentUrl.origin);
    localized.search = target.search;
    localized.hash = target.hash;
    localized.searchParams.set('lang', locale);
    // Gift catalogues are legacy routes and do not accept native draft triggers.
    localized.searchParams.delete('drafts');
    localized.searchParams.delete('preview');
    return localized.href;
  } catch (_) { return ''; }
}

export function normalizeAccessoryCatalogueLinks(doc: Document, current: string): void {
  const locale = doc.documentElement.lang;
  // A no-op outside the explicitly supported accessory catalogue route.
  if (!accessoryCatalogueHref(current, current, locale)) return;
  doc.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(anchor => {
    const href = accessoryCatalogueHref(current, anchor.getAttribute('href') || '', locale);
    if (href) anchor.setAttribute('href', href);
  });
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => normalizeAccessoryCatalogueLinks(document, window.location.href));
}
